import {
  useEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Icon } from './Icon';

const boxClasses =
  'h-[50px] w-full rounded-[10px] border bg-white px-[18px] pt-1 text-[16px] font-normal text-ink shadow-[0_1px_0_rgba(0,0,0,0.02)] outline-none transition-[border-color,box-shadow,background-color] placeholder:text-grey-500';
// Focus or an open list gets a 1px grey-900 outline; red is reserved for errors.
// An errored field darkens to red-700 on focus, so focus stays visible (WCAG 2.4.7)
// without a thicker outline or a glow.
function inputBorderClasses(error?: string, active = false) {
  if (error) return active ? 'border-red-700' : 'border-red-500 focus:border-red-700';
  return active
    ? 'border-grey-900'
    : 'border-black-6 hover:not-focus:border-black/15 focus:border-grey-900';
}

function FieldMessage({ id, error, hint }: { id: string; error?: string; hint?: string }) {
  if (error) {
    return (
      <span
        id={`${id}-error`}
        role="alert"
        className="mt-2 text-[13px] leading-[1.45] font-normal text-red-500">
        {error}
      </span>
    );
  }
  if (!hint) return null;
  return (
    <span id={`${id}-hint`} className="mt-2 text-[13px] leading-[1.45] font-normal text-grey-600">
      {hint}
    </span>
  );
}

export function BusinessFormField({
  id,
  label,
  className,
  error,
  hint,
  ...inputProps
}: {
  id: string;
  label: string;
  className?: string;
  error?: string;
  hint?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'className'>) {
  const descriptionId = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <label
      htmlFor={id}
      className={`flex min-w-0 flex-col text-[14px] leading-[1.35] font-medium text-ink ${className ?? ''}`}>
      <span className="mb-2">{label}</span>
      <input
        id={id}
        required
        aria-invalid={Boolean(error)}
        aria-describedby={descriptionId}
        {...inputProps}
        className={`${boxClasses} ${inputBorderClasses(error)}`}
      />
      <FieldMessage id={id} error={error} hint={hint} />
    </label>
  );
}

export function BusinessSelectField({
  id,
  name,
  label,
  multiple = false,
  options,
  otherValue,
  otherName,
  error,
  className,
  onValueChange,
}: {
  id: string;
  name: string;
  label: string;
  multiple?: boolean;
  options: string[];
  otherValue?: string;
  otherName?: string;
  error?: string;
  className?: string;
  onValueChange?: (name: string) => void;
}) {
  const choices = otherValue ? [...options, otherValue] : options;
  const [selected, setSelected] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  // Only keyboard navigation highlights an option; pointer hover and selection leave rows unchanged.
  const [keyboardNav, setKeyboardNav] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const labelId = `${id}-label`;
  const valueId = `${id}-value`;
  const listId = `${id}-listbox`;
  const optionId = (index: number) => `${id}-option-${index}`;
  const display = (choice: string) => (choice === otherValue ? 'Other' : choice);
  const otherSelected = Boolean(otherValue && selected.includes(otherValue));

  useEffect(() => {
    if (!open) return;
    listRef.current?.focus({ preventScroll: true });
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  useEffect(() => {
    const list = listRef.current;
    const option = list?.children[activeIndex] as HTMLElement | undefined;
    if (!list || !option) return;
    if (option.offsetTop < list.scrollTop) list.scrollTop = option.offsetTop;
    const bottom = option.offsetTop + option.offsetHeight;
    if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
  }, [open, activeIndex]);

  const openList = (index?: number, viaKeyboard = false) => {
    setKeyboardNav(viaKeyboard);
    const firstSelected = choices.findIndex((choice) => selected.includes(choice));
    setActiveIndex(index ?? Math.max(firstSelected, 0));
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const choose = (index: number) => {
    const choice = choices[index]!;
    setActiveIndex(index);
    if (multiple) {
      setSelected((current) =>
        current.includes(choice)
          ? current.filter((value) => value !== choice)
          : choices.filter((value) => value === choice || current.includes(value)),
      );
    } else {
      setSelected([choice]);
      close();
    }
    onValueChange?.(name);
  };

  const onTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      openList(event.key === 'ArrowUp' ? choices.length - 1 : undefined, true);
    }
  };

  const onListKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const last = choices.length - 1;
    const moves: Record<string, number> = {
      ArrowDown: Math.min(activeIndex + 1, last),
      ArrowUp: Math.max(activeIndex - 1, 0),
      Home: 0,
      End: last,
    };
    if (event.key in moves) {
      event.preventDefault();
      setKeyboardNav(true);
      setActiveIndex(moves[event.key]!);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      choose(activeIndex);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'Tab') {
      triggerRef.current?.focus();
      setOpen(false);
    } else if (event.key.length === 1) {
      const key = event.key.toLowerCase();
      const ordered = [...choices.slice(activeIndex + 1), ...choices.slice(0, activeIndex + 1)];
      const match = ordered.find((choice) => display(choice).toLowerCase().startsWith(key));
      if (match) {
        setKeyboardNav(true);
        setActiveIndex(choices.indexOf(match));
      }
    }
  };

  const summary = selected.map(display).join(', ');

  return (
    <div
      ref={rootRef}
      className={`flex min-w-0 flex-col text-[14px] leading-[1.35] font-medium text-ink ${className ?? ''}`}>
      <span id={labelId} className="mb-2">
        {label}
      </span>
      <div className="relative">
        <button
          ref={triggerRef}
          id={id}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          aria-labelledby={`${labelId} ${valueId}`}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
          onClick={() => (open ? setOpen(false) : openList())}
          onKeyDown={onTriggerKeyDown}
          className={`${boxClasses} flex items-center gap-3 text-left ${inputBorderClasses(error, open)}`}>
          <span
            id={valueId}
            className={`min-w-0 flex-1 translate-y-px truncate [text-box-trim:none] ${summary ? '' : 'text-grey-500'}`}>
            {summary || (multiple ? 'Select all that apply' : 'Select an option')}
          </span>
          <Icon
            name="chevron-down"
            tint
            className={`size-5 shrink-0 -translate-y-[3px] text-grey-600 transition-transform motion-reduce:transition-none ${open ? 'rotate-180' : ''}`}
          />
        </button>
        {open && (
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            tabIndex={-1}
            aria-labelledby={labelId}
            aria-multiselectable={multiple || undefined}
            aria-activedescendant={optionId(activeIndex)}
            onKeyDown={onListKeyDown}
            className={`absolute inset-x-0 top-full z-20 mt-2 overflow-y-auto rounded-[10px] border border-black-6 bg-white shadow-[0_12px_32px_rgba(0,0,0,0.12)] outline-none ${multiple ? 'flex max-h-[min(480px,60vh)] flex-col gap-1.5 p-2' : 'max-h-[min(400px,60vh)] p-1.5'}`}>
            {choices.map((choice, index) => {
              const isSelected = selected.includes(choice);
              return (
                <li
                  key={choice}
                  id={optionId(index)}
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    setKeyboardNav(false);
                    choose(index);
                  }}
                  className={`flex min-h-[50px] shrink-0 cursor-pointer items-center gap-3 px-3 py-2.5 text-[16px] font-normal text-ink ${multiple ? 'border border-black/10' : ''} rounded-[10px] ${keyboardNav && index === activeIndex ? 'bg-grey-50' : ''}`}>
                  {multiple && (
                    <span
                      aria-hidden="true"
                      className={`flex size-5 shrink-0 items-center justify-center rounded-full border ${isSelected ? 'border-red-500 bg-red-500 text-white' : 'border-black/20 bg-white'}`}>
                      {isSelected && <Icon name="check" tint className="size-3.5" />}
                    </span>
                  )}
                  <span className="min-w-0 flex-1">{display(choice)}</span>
                  {!multiple && isSelected && (
                    <Icon name="check" tint className="size-4 shrink-0 text-red-500" />
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {selected.map((value) => (
        <input key={value} type="hidden" name={name} value={value} />
      ))}
      {otherSelected && otherName && (
        <input
          id={`${id}-other`}
          name={otherName}
          aria-label={`${label}: other`}
          placeholder="Tell us more"
          maxLength={200}
          className={`${boxClasses} mt-3 ${inputBorderClasses()}`}
        />
      )}
      <FieldMessage id={id} error={error} />
    </div>
  );
}

export function BusinessFormSuccess({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[580px] animate-[waitlist-success-in_500ms_cubic-bezier(0.22,1,0.36,1)_both] flex-col items-center justify-center text-center motion-reduce:animate-none">
      <div className="flex size-16 animate-[waitlist-success-ring_600ms_cubic-bezier(0.22,1,0.36,1)_150ms_both] items-center justify-center rounded-full bg-red-500 text-white shadow-[0_14px_35px_rgba(215,0,10,0.2)] motion-reduce:animate-none">
        <Icon
          name="check"
          tint
          className="size-7 animate-[waitlist-success-check_400ms_ease-out_350ms_both] motion-reduce:animate-none"
        />
      </div>
      <h2 className="mt-9 text-[36px] leading-[1.08] font-semibold tracking-[-0.035em] text-ink md:text-[46px]">
        {title}
      </h2>
      <p className="mt-6 max-w-md text-body leading-[1.6] text-grey-700">{children}</p>
    </div>
  );
}
